import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import { COLORS, ELEMENTS, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    text = "text",
    loginCredentailURL = "loginCredentailURL",
    openTradeAccountURL = "openTradeAccountURL",
    fullWidthButton = "fullWidthButton",
    showRequestLoginCredentials = "showRequestLoginCredentials",
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        allowCreateAccount,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
            returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.loginCredentailURL]: string;
        [fields.openTradeAccountURL]: string;
        [fields.fullWidthButton]: boolean;
        [fields.showRequestLoginCredentials]: boolean;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    wrapper?: InjectableCss;
    signInCreateNewAccountGridContainerWrapper?: GridContainerProps;
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItemsubContent?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    newTopGridItem?: GridItemProps;
    existingTopGridItem?: GridItemProps;
    customerText?: InjectableCss;
}
export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    wrapper: {
        css: css`
            background-color: #ffffff;
            overflow: hidden;
            display: flex;
            flex-direction: column;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: auto !important;
                        `,
                        css`
                            min-height: auto !important;
                        `,
                        css`
                            min-height: auto !important;
                        `,
                        css`
                            min-height: 414px;
                        `,
                        css`
                            min-height: 414px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    signInCreateNewAccountGridContainerWrapper: {
        css: css`
            flex-grow: 1;
        `,
    },
    signInCreateNewAccountGridContainer: {
        gap: 20,
        css: css`
            display: flex;
            height: 100%;
            padding: 38px 33px 112px 32px;
            border: 1px solid ${getColor("common.border")};
            border-top: none;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 35px !important;
                        `,
                        css`
                            padding: 35px !important;
                        `,
                        css`
                            padding: 35px !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    signInCreateNewAccountTitle: {
        variant: "h4",
        css: css`
            padding: 20px 30px;
            margin-bottom: 0;
            font-family: ${TYPOGRAPHY.headings.fontFamily};
            font-style: ${TYPOGRAPHY.headings.fontStyle};
            font-size: ${TYPOGRAPHY.cardSectionTitle.size};
            line-height: ${TYPOGRAPHY.lineHeight.medium};
            color: ${getColor("text.main")};
            background-color: ${getColor("common.backgroundContrast")};
            border: 1px solid ${getColor("common.border")};
            border-bottom: none;
        `,
    },
    signInCreateNewAccountTitleGridItem: {
        width: 12,
        css: css`
            display: block;
            padding: 0;
        `,
    },
    signInCreateNewAccountTextGridItem: {
        width: 12,
        css: css`
            font-weight: 700;
        `,
    },
    signInCreateNewAccountTextGridItemsubContent: {
        width: 12,
    },
    signInCreateNewAccountButtonGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            padding-top: 12px;
            padding-bottom: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            align-items: flex-start;
                        `,
                        null,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                            align-items: flex-end;
                        `,
                        css`
                            flex-direction: column;
                            justify-content: space-between;
                            align-items: flex-start;
                            

                        `,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                            align-items: flex-end;
                        

                        `,
                    ],
                    "min",
                )}
        `,
    },
    checkoutAsGuestButton: {
        variant: "tertiary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                             margin-bottom: 1rem;
                        `,
                        null,
                        css`
                            margin-right: 1rem;
                            width: 100%;
                        `,
                        css`
                         margin-bottom: 1rem;
                        `,
                        css`
                            margin-right: 1rem;
                        `,
                        
                    ],
                    "min",
                )}
        `,
    },
    createNewAccountButton: {
        variant: "primary",
        css: css`
            color: ${getColor("text.main")};
            background: ${getColor("text.accent")};

        
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    newTopGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-top: 15px;
            padding-bottom: 0;
        `,
    },
    existingTopGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-top: 32px;
            padding-bottom: 0;
        `,
    },
    customerText: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.base};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            color: ${getColor("text.main")};
        `,
    },
    signInCreateNewAccountText: {
        fontFamily: "MarkProBold",
    },
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
const StyledForm = getStyledWrapper("form");
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields,
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    // if (!allowCreateAccount) {
    //     return null;
    // }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(fields.loginCredentailURL);
    };

    const createButtonCss =css`
        ${() => {
            // MGBW uses theme-level button.base.hover tokens from Start.tsx
            // Surya/GV need custom hover here because they don't define those tokens
            if (isMgbwWebsite()) {
                return ''; // Let Start.tsx theme handle it
            }
            return css`
                border-radius: ${ELEMENTS.button.base.borderRadius};
                
                &:hover {
                    background: ${getColor("text.accent")};
                    span[class^="TypographyStyle"] {
                        color: ${getColor("secondary")};
                    }
                }
                > span {
                    font-family: ${TYPOGRAPHY.button.fontFamily} !important;
                    font-style: normal !important;
                    font-weight: ${TYPOGRAPHY.button.weight} !important;
                    font-size: ${TYPOGRAPHY.button.size} !important;
                    line-height: ${TYPOGRAPHY.button.lineHeight} !important;
                    color: ${getColor("primary.contrast")} !important;
                }
            `;
        }}
      `;

    const handleOpentradeAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(fields.openTradeAccountURL);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.signInCreateNewAccountTitle}>{translate("Create an Account")}</Typography>
            <StyledWrapper {...styles.signInCreateNewAccountGridContainerWrapper}>
                <GridContainer {...styles.signInCreateNewAccountGridContainer}>
                    {fields.showRequestLoginCredentials && (
                        <>
                            <GridItem {...styles.newTopGridItem}>
                                {/* <Typography variant="h4" {...styles.signInCreateNewAccountText}>
                                    {fields.text}
                                </Typography> */}

                                <Typography {...styles.customerText}>{translate("If you are an Existing Customer")}</Typography>
                            </GridItem>
                            <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                                {createAccountUrl && (
                                    <Button
                                        {...styles.createNewAccountButton}
                                        onClick={handleCreateNewAccount}
                                        css={createButtonCss}
                                        data-test-selector="signInCreateNewAccount_createNewAccount"
                                    >
                                        {translate("Request Login Credentials")}
                                    </Button>
                                )}
                            </GridItem>
                        </>
                    )}
                    
                    <GridItem {...styles.existingTopGridItem}>
                        {/* <Typography variant="h4" {...styles.signInCreateNewAccountText}>
                            {fields.text}
                        </Typography> */}

                        <Typography {...styles.customerText}>{translate("If you are a New Customer")}</Typography>
                    </GridItem>
                    <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                        {createAccountUrl && (
                            <Button
                                {...styles.createNewAccountButton}
                                css={createButtonCss}
                                onClick={handleOpentradeAccount}
                                data-test-selector="signInCreateNewAccount_createNewAccount"
                            >
                                {translate("Open a Trade Account")}
                            </Button>
                        )}
                    </GridItem>
                    {allowGuestCheckout && (<GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                        <Button
                                {...styles.checkoutAsGuestButton}
                                onClick={handleCheckoutAsGuest}
                                data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                            >
                                {translate("Checkout as Guest")}
                            </Button>
                    </GridItem>)}
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage","SignInOverlay"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.text,
                displayName: "Main Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
                sortOrder:0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.loginCredentailURL,
                displayName: "Login Credentials URL",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.openTradeAccountURL,
                displayName: "Open a Trade Account URL",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.showRequestLoginCredentials,
                displayName: "Show Request Login Credentials",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
